import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { CalculatorProvider } from './contexts/CalculatorContext';
import { getDefaultTaxRate } from './store/slices/taxSlice';
import { updateTaxRate } from './store/slices/cartSlice';
import theme from './config/theme';

import Layout from '@components/layout/Layout';
import ProtectedRoute from '@components/ProtectedRoute';
import LoadingScreen from '@components/common/LoadingScreen';
import ChunkErrorBoundary from '@components/ChunkErrorBoundary';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const CategoriesPage = lazy(() => import('./pages/admin/CategoriesPage'));
const AttributesPage = lazy(() => import('./pages/admin/AttributesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ImagesPage = lazy(() => import('./pages/admin/ImagesPage'));

import ProductCatalogPage from './pages/ProductCatalogPage';
import ProductDetailsPage from '@pages/ProductDetailsPage';
import OrdersPage from '@pages/OrdersPage';
import ReturnsPage from '@pages/ReturnsPage';
import WalletPage from '@pages/WalletPage';
import AccountSettingsPage from '@pages/AccountSettingsPage';
import PendingApprovalPage from '@pages/PendingApprovalPage';
import CartPage from './pages/CartPage';
import CheckoutPage from '@pages/CheckoutPage';
import OrderConfirmationPage from '@pages/OrderConfirmationPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ReturnDetailPage from './pages/ReturnDetailPage';

import AdminProductsPage from './pages/admin/ProductsPage';
const CreateProductPage = lazy(() => import('./pages/admin/CreateProductPage'));
const EditProductPage = lazy(() => import('./pages/admin/EditProductPage'));
const UserDetailsPage = lazy(() => import('./pages/admin/UserDetailsPage'));
import AdminPricingPage from './pages/admin/PricingPage';
import AdminCouponsPage from './pages/admin/CouponsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminReturnsPage from './pages/admin/AdminReturnsPage';
import AdminInventoryPage from './pages/admin/InventoryPage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import BankDetailsPage from './pages/admin/BankDetailsPage';
import StockMovementsPage from './pages/admin/StockMovementsPage';
import AdminReturnDetailPage from './pages/admin/AdminReturnDetailPage';
import CouponUsagesPage from './pages/admin/CouponUsagesPage';
import ShippingMethodsPage from './pages/admin/ShippingMethodsPage';
import TaxRatesPage from './pages/admin/TaxRatesPage';
import SiteImagesPage from './pages/admin/SiteImagesPage';

/**
 * Main App component that handles routing and theme
 * @returns {JSX.Element} The App component
 */
function App() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { taxRate } = useSelector((state) => state.tax);

  useEffect(() => {
    dispatch(getDefaultTaxRate());
  }, [dispatch]);

  useEffect(() => {
    if (taxRate?.rate) {
      dispatch(updateTaxRate(parseFloat(taxRate.rate)));
    }
  }, [taxRate, dispatch]);

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language);
    } else if (user) {
      const browserLang = navigator.language.split('-')[0];
      const supportedLangs = ['en', 'de', 'fr', 'it'];
      const lang = supportedLangs.includes(browserLang) ? browserLang : 'en';

      const userData = JSON.parse(localStorage.getItem('user')) || {};
      userData.language = lang;
      localStorage.setItem('user', JSON.stringify(userData));

      i18n.changeLanguage(lang);
    }
  }, [user, i18n]);

  if (user && (user.status === 'pending' || user.status === 'rejected')) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
        />
        <AuthProvider>
          <CartProvider>
            <CalculatorProvider>
              <ChunkErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <Layout>
                    <Routes>
                      <Route
                        path="/approval-status"
                        element={<PendingApprovalPage />}
                      />
                      {/* Redirect all other paths to /approval-status */}
                      <Route
                        path="*"
                        element={
                          <Navigate
                            to="/approval-status"
                            replace
                          />
                        }
                      />
                    </Routes>
                  </Layout>
                </Suspense>
              </ChunkErrorBoundary>
            </CalculatorProvider>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
      />
      <AuthProvider>
        <CartProvider>
          <CalculatorProvider>
            <Suspense fallback={<LoadingScreen />}>
              <Layout>
                <Routes>
                  {/* Redirect from home based on auth status */}
                  <Route
                    path="/"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <Navigate
                          to="/login"
                          replace
                        />
                      )
                    }
                  />

                  {/* Public routes: if user is logged in (and approved/other), redirect them away */}
                  <Route
                    path="/login"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <LoginPage />
                      )
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <RegisterPage />
                      )
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <ForgotPasswordPage />
                      )
                    }
                  />
                  <Route
                    path="/reset-password/:token"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <ResetPasswordPage />
                      )
                    }
                  />

                  {/* Approval status route for approved or unauthenticated users */}
                  <Route
                    path="approval-status"
                    element={
                      user ? (
                        <Navigate
                          to="/products"
                          replace
                        />
                      ) : (
                        <Navigate
                          to="/login"
                          replace
                        />
                      )
                    }
                  />

                  {/* Protected buyer routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['buyer', 'admin']}>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute allowedRoles={['buyer', 'admin']}>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected routes for approved buyers */}
                  <Route
                    path="products"
                    element={
                      <ProtectedRoute allowedRoles={['buyer', 'admin']}>
                        <ProductCatalogPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="products/:id"
                    element={
                      <ProtectedRoute allowedRoles={['buyer', 'admin']}>
                        <ProductDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="cart"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <CartPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="checkout"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <CheckoutPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="checkout/confirmation"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <OrderConfirmationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="orders"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <OrdersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="returns"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <ReturnsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="returns/:returnId"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <ReturnDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="wallet"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <WalletPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer', 'admin']}
                        requiredStatus="approved"
                      >
                        <AccountSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="orders/:orderId"
                    element={
                      <ProtectedRoute
                        allowedRoles={['buyer']}
                        requiredStatus="approved"
                      >
                        <OrderDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <UsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users/:userId"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <UserDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CategoriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/attributes"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AttributesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/products"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminProductsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/products/create"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CreateProductPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/products/edit/:id"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <EditProductPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/images"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <ImagesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/pricing"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminPricingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/coupons"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminCouponsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/coupon-usages"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CouponUsagesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/orders"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminOrdersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/returns"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminReturnsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/inventory"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminInventoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/orders/:orderId"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminOrderDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/bank-details"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <BankDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/stock-movements"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <StockMovementsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/returns/:returnId"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminReturnDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/shipping"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <ShippingMethodsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tax"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <TaxRatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/site-images"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <SiteImagesPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Fallback routes */}
                  <Route
                    path="/404"
                    element={<NotFoundPage />}
                  />
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to="/404"
                        replace
                      />
                    }
                  />
                </Routes>
              </Layout>
            </Suspense>
          </CalculatorProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
